// Ported from design/project/components/icons.jsx. All icons inherit currentColor.
import type { CSSProperties } from 'react';

type IconProps = {
  s?: number;
  strokeWidth?: number;
  style?: CSSProperties;
  className?: string;
};

const wrap = (children: React.ReactNode, p: IconProps, fill: string = 'none') => (
  <svg
    width={p.s ?? 22}
    height={p.s ?? 22}
    viewBox="0 0 24 24"
    fill={fill}
    stroke="currentColor"
    strokeWidth={p.strokeWidth ?? 1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={p.style}
    className={p.className}
  >
    {children}
  </svg>
);

export const Icon = {
  moon: (p: IconProps = {}) => wrap(<path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" />, p),
  bottle: (p: IconProps = {}) =>
    wrap(
      <>
        <path d="M10 2h4M10 4h4M9 7h6l-.6 3.5a4 4 0 0 1-.1 1.6L16 19a3 3 0 0 1-3 3h-2a3 3 0 0 1-3-3l1.7-6.9a4 4 0 0 1-.1-1.6L9 7z" />
        <path d="M10 13h4" strokeOpacity="0.5" />
      </>,
      p,
    ),
  breast: (p: IconProps = {}) =>
    wrap(
      <>
        <path d="M5 13a7 7 0 0 1 14 0v1a3 3 0 0 1-3 3h-8a3 3 0 0 1-3-3v-1z" />
        <circle cx="12" cy="13" r="1.4" fill="currentColor" stroke="none" />
      </>,
      p,
    ),
  solid: (p: IconProps = {}) =>
    wrap(
      <>
        <path d="M3 11h18a8 8 0 0 1-8 8h-2a8 8 0 0 1-8-8z" />
        <path d="M17 4l-2 6" />
      </>,
      p,
    ),
  pump: (p: IconProps = {}) =>
    wrap(
      <>
        <rect x="4" y="9" width="11" height="10" rx="2" />
        <path d="M15 12h4v4h-4z" />
        <path d="M8 6c0-1 1-2 2-2s2 1 2 2" />
        <path d="M9 9V7" />
      </>,
      p,
    ),
  diaper: (p: IconProps = {}) =>
    wrap(
      <>
        <path d="M3 7h18l-2 6a5 5 0 0 1-5 4h-4a5 5 0 0 1-5-4L3 7z" />
        <path d="M3 7l2 3M21 7l-2 3" />
      </>,
      p,
    ),
  drop: (p: IconProps = {}) =>
    wrap(<path d="M12 3c-3.5 4-6 7-6 10a6 6 0 0 0 12 0c0-3-2.5-6-6-10z" />, p),
  pin: (p: IconProps = {}) =>
    wrap(
      <>
        <path d="M12 21s7-6 7-12a7 7 0 0 0-14 0c0 6 7 12 7 12z" />
        <circle cx="12" cy="9" r="2.5" />
      </>,
      p,
    ),
  chevR: (p: IconProps = {}) => wrap(<path d="M9 5l7 7-7 7" />, p),
  chevL: (p: IconProps = {}) => wrap(<path d="M15 5l-7 7 7 7" />, p),
  chevD: (p: IconProps = {}) => wrap(<path d="M5 9l7 7 7-7" />, p),
  chevU: (p: IconProps = {}) => wrap(<path d="M5 15l7-7 7 7" />, p),
  plus: (p: IconProps = {}) =>
    wrap(
      <>
        <path d="M12 5v14" />
        <path d="M5 12h14" />
      </>,
      p,
    ),
  x: (p: IconProps = {}) =>
    wrap(
      <>
        <path d="M6 6l12 12" />
        <path d="M18 6L6 18" />
      </>,
      p,
    ),
  check: (p: IconProps = {}) => wrap(<path d="M5 13l4 4L19 7" />, p),
  play: (p: IconProps = {}) =>
    wrap(<path d="M7 5v14l12-7L7 5z" />, { ...p, strokeWidth: 0 }, 'currentColor'),
  pause: (p: IconProps = {}) =>
    wrap(
      <>
        <rect x="6" y="5" width="4" height="14" />
        <rect x="14" y="5" width="4" height="14" />
      </>,
      { ...p, strokeWidth: 0 },
      'currentColor',
    ),
  stop: (p: IconProps = {}) =>
    wrap(<rect x="6" y="6" width="12" height="12" rx="2" />, { ...p, strokeWidth: 0 }, 'currentColor'),
  home: (p: IconProps = {}) =>
    wrap(
      <>
        <path d="M3 11l9-7 9 7" />
        <path d="M5 10v10h14V10" />
      </>,
      p,
    ),
  chart: (p: IconProps = {}) =>
    wrap(
      <>
        <rect x="3" y="13" width="4" height="8" />
        <rect x="10" y="8" width="4" height="13" />
        <rect x="17" y="4" width="4" height="17" />
      </>,
      p,
    ),
  stats: (p: IconProps = {}) =>
    wrap(
      <>
        <path d="M4 19V5" />
        <path d="M4 19h16" />
        <path d="M7 16l4-5 3 3 6-7" />
      </>,
      p,
    ),
  more: (p: IconProps = {}) =>
    wrap(
      <>
        <circle cx="5" cy="12" r="1.8" />
        <circle cx="12" cy="12" r="1.8" />
        <circle cx="19" cy="12" r="1.8" />
      </>,
      { ...p, strokeWidth: 0 },
      'currentColor',
    ),
  gear: (p: IconProps = {}) =>
    wrap(
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 9 4.6 1.7 1.7 0 0 0 10 3.1V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9c.3.6.9 1 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
      </>,
      p,
    ),
  bell: (p: IconProps = {}) =>
    wrap(
      <>
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 8 3 8H3s3-1 3-8z" />
        <path d="M10 20a2 2 0 0 0 4 0" />
      </>,
      p,
    ),
  heart: (p: IconProps = {}) =>
    wrap(
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l8.84 8.84 8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />,
      p,
    ),
  clock: (p: IconProps = {}) =>
    wrap(
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>,
      p,
    ),
  edit: (p: IconProps = {}) =>
    wrap(
      <>
        <path d="M14 4l6 6L8 22H2v-6L14 4z" />
        <path d="M13 5l6 6" />
      </>,
      p,
    ),
  info: (p: IconProps = {}) =>
    wrap(
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5" />
        <circle cx="12" cy="8" r="0.5" fill="currentColor" stroke="none" />
      </>,
      p,
    ),
  trash: (p: IconProps = {}) =>
    wrap(
      <>
        <path d="M4 7h16" />
        <path d="M9 7V4h6v3" />
        <path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
      </>,
      p,
    ),
  undo: (p: IconProps = {}) =>
    wrap(
      <>
        <path d="M3 12h12a5 5 0 0 1 0 10H9" />
        <path d="M7 8L3 12l4 4" />
      </>,
      p,
    ),
  user: (p: IconProps = {}) =>
    wrap(
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </>,
      p,
    ),
  share: (p: IconProps = {}) =>
    wrap(
      <>
        <path d="M12 16V4" />
        <path d="M7 9l5-5 5 5" />
        <path d="M5 14v6h14v-6" />
      </>,
      p,
    ),
  poop: (p: IconProps = {}) =>
    wrap(
      <path d="M9 9a3 3 0 0 1 3-3c0 2 2 2 2 4 0 0 3 0 3 3a3 3 0 0 1-3 3h0a3 3 0 0 1 3 3 3 3 0 0 1-3 3H10a3 3 0 0 1-3-3 3 3 0 0 1 3-3 3 3 0 0 1-3-3c0-2 2-3 2-4z" />,
      p,
    ),
  sparkle: (p: IconProps = {}) =>
    wrap(
      <>
        <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3z" />
        <path d="M19 16l.7 1.8L21 18l-1.3.2-.7 1.8-.7-1.8L17 18l1.3-.2.7-1.8z" />
      </>,
      p,
    ),
  cloud: (p: IconProps = {}) =>
    wrap(<path d="M7 17a4 4 0 0 1 0-8 5 5 0 0 1 9.6-1A4.5 4.5 0 0 1 17 17H7z" />, p),
  dot: (p: IconProps = {}) =>
    wrap(<circle cx="12" cy="12" r="3" />, { ...p, strokeWidth: 0 }, 'currentColor'),
};
